import {
  trackWingsCupClick,
  trackWingsCupImpression,
} from "@/events/wingsCupMixpanel";
import { cn } from "@/lib/utils";
import { Fragment, useEffect, useState } from "react";
import { useLocation, useNavigationType } from "react-router";

import { type WingsCup, useWingsCup } from "./useWingsCup";
import "./wingsCup.css";
import {
  WINGS_CUP_CONFIG,
  WINGS_CUP_UNITS,
  type WingsCupTrackedUnit,
  type WingsCupUnit,
} from "./wingsCupConfig";

const { logo, logoWidth, logoHeight, fcPoints, logoAlt, fcPointsAlt } =
  WINGS_CUP_CONFIG.assets;
const LINK_PROPS = { target: "_blank", rel: "noopener sponsored" } as const;
// The images carry their natural sizes, so their space is held while they
// load; the CSS sets the drawn width.

/*
  Units already counted on this page view. A page view starts with a real
  navigation (a link, back or forward); a replace keeps the view, since that
  is a page rewriting its own URL: filters, sort, or the evolution builder
  adding the version to the path.
*/
const impressions = {
  key: "",
  view: 0,
  units: new Set<WingsCupTrackedUnit>(),
};

/** Fires the impression once per unit per page view, at 50% visible. */
const useImpression = (unit: WingsCupTrackedUnit, cup: WingsCup | null) => {
  const { key } = useLocation();
  const navigationType = useNavigationType();
  const [element, setElement] = useState<Element | null>(null);
  const country = cup?.country;
  const lang = cup?.lang;

  useEffect(() => {
    if (impressions.key !== key) {
      impressions.key = key;
      if (navigationType !== "REPLACE") {
        impressions.view++;
        impressions.units.clear();
      }
    }
    const view = impressions.view;
    if (!element || !country || !lang || impressions.units.has(unit)) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((e) => e.intersectionRatio >= 0.5)) return;
        observer.disconnect();
        if (impressions.view !== view || impressions.units.has(unit)) return;
        impressions.units.add(unit);
        trackWingsCupImpression({ unit, country, lang });
      },
      { threshold: 0.5 },
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, [element, key, navigationType, unit, country, lang]);

  return setElement;
};

/** The copy's only markup: <b> for the points and <br> for a line break. */
const renderCopy = (text: string) =>
  text
    .split(/(<b>.*?<\/b>|<br>)/)
    .map((part, i) =>
      part === "<br>" ? (
        <br key={i} />
      ) : part.startsWith("<b>") ? (
        <b key={i}>{part.slice(3, -4)}</b>
      ) : (
        <Fragment key={i}>{part}</Fragment>
      ),
    );

/**
 * A Red Bull Wings Cup Open unit. Renders nothing outside the campaign (see
 * useWingsCup), so it can sit in a grid or list without leaving a gap.
 */
export const WingsCupBanner = ({
  unit,
  className,
}: {
  unit: WingsCupUnit;
  /** E.g. col-span-full, to span the grid it sits in. */
  className?: string;
}) => {
  const cup = useWingsCup();
  const isNav = unit === "navigation";
  const trackedUnit: WingsCupTrackedUnit = isNav ? "navigation-card" : unit;
  // One impression for the whole menu; only clicks split card from link.
  const impressionRef = useImpression(unit, cup);
  if (!cup) return null;

  const { copy, country, lang, url } = cup;
  const keys = WINGS_CUP_UNITS[unit];
  const onClick = () =>
    trackWingsCupClick({ unit: trackedUnit, country, lang });

  const badge = (
    <div className="rbwc-badge">
      <img src={fcPoints} width={242} height={210} alt={fcPointsAlt} />
      <span>
        <strong>+100</strong>
        {copy.badge}
      </span>
    </div>
  );
  const cta = (
    <a className="rbwc-cta" href={url} {...LINK_PROPS} onClick={onClick}>
      {copy.cta}
    </a>
  );

  return (
    <aside
      ref={impressionRef}
      className={cn("rbwc", isNav ? "rbwc--nav" : `rbwc--${unit}`, className)}
      data-unit={unit}
      lang={lang}
    >
      <div className="rbwc-tags">
        <span className="rbwc-tag">{WINGS_CUP_CONFIG.ageLimit}</span>
        <span className="rbwc-tag">{copy.tag}</span>
      </div>
      <img
        className="rbwc-logo"
        src={logo}
        width={logoWidth}
        height={logoHeight}
        alt={logoAlt}
      />
      <div className="rbwc-body">
        <span className="rbwc-free">{copy.free}</span>
        <p className="rbwc-hd">{renderCopy(copy[keys.headline])}</p>
        <p className="rbwc-sub">{copy[keys.sub]}</p>
        {isNav && (
          <div className="rbwc-row">
            {cta}
            {badge}
          </div>
        )}
      </div>
      {!isNav && badge}
      {!isNav && cta}
    </aside>
  );
};

/**
 * The menu's "Tournaments" group: one link to the sign-up page with a
 * FREE/GRATIS badge and the AD/REKLAME label. Desktop sits in the site map;
 * mobile in the drawer.
 */
export const WingsCupMenuLink = ({
  variant,
}: {
  variant: "desktop" | "mobile";
}) => {
  const cup = useWingsCup();
  const impressionRef = useImpression("navigation", cup);
  if (!cup) return null;

  const { copy, country, lang, url } = cup;
  const onClick = () =>
    trackWingsCupClick({ unit: "navigation-link", country, lang });

  return (
    <div
      className={
        variant === "desktop" ? "flex flex-col gap-y-1" : "flex flex-col pt-2"
      }
      lang={lang}
    >
      {/* The site map's group heading, so it reads as one of them. Not
          through cn(): next to a text colour it would merge text-body away. */}
      <span
        className={`py-1 font-oswald text-body tracking-wide uppercase text-foreground ${variant === "desktop" ? "px-2.5" : "px-4"}`}
      >
        {copy.menuHeading}
      </span>
      <a
        ref={impressionRef}
        href={url}
        {...LINK_PROPS}
        onClick={onClick}
        data-unit="navigation"
        className={cn(
          "rbwc-menu-item rounded-lg transition-colors hover:bg-surface",
          variant === "desktop" ? "h-8 px-2.5" : "mx-2 h-11 px-2",
        )}
      >
        <img src={logo} width={logoWidth} height={logoHeight} alt="" />
        {copy.menuItem}
        <span className="rbwc-menu-badge">{copy.menuBadge}</span>
        <span className="rbwc-menu-tag">{WINGS_CUP_CONFIG.ageLimit}</span>
        <span className="rbwc-menu-tag">{copy.tag}</span>
      </a>
    </div>
  );
};
